import { Box } from '@factorialco/deprecated-design-system'
import { Currency } from 'dinero.js'
import { AvailableModules } from 'generated/featureDefinitions'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useModuleAvailabilities } from 'modules/core/hooks/useModuleAvailabilities'
import { ModuleAvailabilityType } from 'modules/core/hooks/useModuleAvailability'
import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import hasFeature from 'modules/core/lib/hasFeature'
import { isModuleAvailabilityEnabled } from 'modules/core/lib/isModuleAvailabilityEnabled'

import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import contractVersionsPayrollPolicies from 'modules/contracts/store/collections/contractVersionsPayrollPolicies'
import PayrollSummaryWidget from 'modules/payroll_bundle/components/PayrollSummaryWidget'
import { hasInHousePayroll } from 'modules/payroll_bundle/hooks/useInhousePayroll'
import { usePayrollContextlessPolicies } from 'modules/payroll_bundle/hooks/usePayrollContextlessPolicies'
import filterActiveEmployeesByPolicy from 'modules/payroll_bundle/lib/filterActiveEmployeesByPolicy'

import { usePayrollBundlePageContext } from '../PayrollBundle'
import { FinancialWorkspaceBanner } from './FinancialWorkspaceBanner'
import IncidencesSummary from './IncidencesSummary'
import PaymentSummary from './PaymentSummary'
import RegularCycleSummary from './RegularCycleSummary'
import UpdatesActivation from './UpdateActivation'

interface Props {
  contractVersions: ContractVersions
}

const PolicyPeriodDashboardContent = ({ contractVersions }: Props) => {
  const { selectedPolicy, policyPeriod, employees } = usePayrollBundlePageContext()
  const {
    company: { currency: companyCurrency },
  } = useCurrentSession()
  const periodStartDate = moment(policyPeriod?.attributes.starts_on)
  const periodEndDate = moment(policyPeriod?.attributes.ends_on)
  const { canSeeCompanyPayrollResults, canManagePayrollIncidences } =
    usePayrollContextlessPolicies()

  const activeEmployees = filterActiveEmployeesByPolicy({
    employees,
    policy: selectedPolicy,
    contractVersions,
    contractVersionsPayrollPolicies,
    period: {
      startsOn: periodStartDate,
      endsOn: periodEndDate,
    },
  })

  const currencies = [
    ...new Set(activeEmployees.map((employee) => employeeLegalEntity(employee).currency)),
  ].sort()

  const currency = (currencies[0] || companyCurrency) as Currency

  const canSeeIncidences = (moduleAvailabilities: ModuleAvailabilityType[]) => {
    if (hasFeature('dev_packaging_paywalls')) {
      return (
        isModuleAvailabilityEnabled(moduleAvailabilities, AvailableModules.payroll) &&
        canManagePayrollIncidences
      )
    }

    return canManagePayrollIncidences
  }

  const { moduleAvailabilities } = useModuleAvailabilities()
  const authorizedToSeeIncidences = canSeeIncidences(moduleAvailabilities)
  const canSeePayrollBundleIncidences =
    (hasFeature('factorial_professionals') || hasInHousePayroll()) && authorizedToSeeIncidences

  return (
    <Box alignSelf="center" width="full">
      <Box gap="s24">
        <FinancialWorkspaceBanner contractVersions={contractVersions} />
        <RegularCycleSummary />
      </Box>
      <Box gap="s32" paddingLeft="s12" paddingRight="s12">
        <Box flexDirection="row" justifyContent="spaceBetween" gap="s32" paddingTop="s32">
          {canSeeCompanyPayrollResults && <PayrollSummaryWidget currency={currency} />}
          {canSeePayrollBundleIncidences && <IncidencesSummary />}
          {!hasFeature('inhouse_payroll') &&
            !hasFeature('factorial_professionals') &&
            authorizedToSeeIncidences && <UpdatesActivation isLoading={false} />}
        </Box>
        {canSeeCompanyPayrollResults && <PaymentSummary currency={currency} />}
      </Box>
    </Box>
  )
}

export default PolicyPeriodDashboardContent
